import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus, type RoleKey } from "@/shared/lib/rbac";
import { ApiError } from "@/shared/api";
import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

import { bizAdvisorService } from "../../api/bizAdvisorService";
import type { Play } from "../../api/bizAdvisorService";
import { assembleDoc, docTypeForEngine } from "../../api/dispatchDoc";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engine: string; // cs_kb | copy
  play: Play;
  diagnosisId: number;
  onSaved?: () => void;
}

/**
 * 文本类派引擎「存入知识库」确认层(B3 D4:确认即审核)。
 * 预览军师拼好的文档(可编辑)+ tag + 诚实计费提示 → 复用 KB ingest 正门(dispatch-kb,需 MANAGER+)。
 */
export function KbSaveDialog({ open, onOpenChange, engine, play, diagnosisId, onSaved }: Props) {
  const { user } = useAuth();
  const canSave = isTenantManagerPlus(user?.role as RoleKey | undefined);

  const [title, setTitle] = useState(play.title);
  const [content, setContent] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [saving, setSaving] = useState(false);

  // 每次打开(或换 play/engine)重置为军师拼好的文档。
  useEffect(() => {
    if (open) {
      setTitle(play.title);
      setContent(assembleDoc(engine, play.artifacts));
      setTagsRaw("");
    }
  }, [open, engine, play]);

  const save = async () => {
    setSaving(true);
    try {
      const tags = tagsRaw
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean);
      await bizAdvisorService.dispatchKb(diagnosisId, {
        playKey: play.multiplier,
        engine,
        docType: docTypeForEngine(engine),
        title,
        content,
        tags,
      });
      toast({
        title: "已存入知识库",
        // ★0926:kb.upload 是 FREE(见下方说明文字头注),成功提示与说明文字同口径 —— 不扣积分。
        description: "已建草稿并开始切分 / 向量化,存入知识库不扣积分。",
      });
      onSaved?.();
      onOpenChange(false);
    } catch (e: unknown) {
      // 0926 修复波 P13:走全站统一的 errorToast(人话 + 错误码/排查号),不再把裸 e.message 甩给用户。
      // 403 保留这一处的语境话术(比通用「没有权限」更具体)。
      if (e instanceof ApiError && e.httpStatus === 403) {
        toast({
          title: "存入知识库失败",
          description: "需经理及以上权限才能存入知识库。",
          variant: "destructive",
        });
      } else {
        toast(errorToast(e, { fallbackTitle: "存入知识库失败" }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>存入知识库 · 确认</DialogTitle>
          <DialogDescription>
            把该打法的话术存进知识库(走 KB 正门,自动切分 / 向量化,客服机器人随后可检索引用)。
            确认即审核,落库后可在知识库页查看 / 删除。<b>存入知识库不扣积分。</b>
            {/* ★ 2026-08-26 批 L 核实:`kb.upload`(action_type=kb_upload)在 xb_price_sku 里是
                pricing_mode=FREE —— 老板拍板的免费动作之一。原文写「按真实成本扣 ai_kb 积分」,
                是对着用户虚报一笔不存在的费用,而且会劝退一个免费功能。
                同族的另一处(AI 润色标「约 55~70 积分」)由 projects-06 真点了一次核实:扣 0。 */}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="kb-title">文档标题</Label>
            <Input id="kb-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kb-content">文档内容(可编辑)</Label>
            <Textarea
              id="kb-content"
              className="min-h-[180px] font-mono text-xs"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kb-tags">标签(逗号分隔,可空)</Label>
            <Input
              id="kb-tags"
              placeholder="如:异议处理, 价格"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
            />
          </div>
          {!canSave && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
              需经理及以上权限才能存入知识库;你可复制内容请管理员存入。
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            取消
          </Button>
          <Button onClick={() => void save()} disabled={!canSave || saving || !content.trim()}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            确认存入
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
